import { useMemo, useState } from "react"
import { MultiSelect } from "procurement/design"
import { useGetBudgetCentersQuery } from "shared/redux/endpoints/procurementV2"
import { Button } from "../../ui"
import { regionFromCenterCode } from "../../constants"

// Client-side center narrowing for one chart; the page-wide filter still drives the API.
export const useCenterPicker = (items) => {
  const [selected, setSelected] = useState([])
  const { data: centers = [] } = useGetBudgetCentersQuery({ isActive: true })

  const options = useMemo(() => {
    const regionById = Object.fromEntries(centers.map((c) => [String(c.id), regionFromCenterCode(c.centerCode || "")]))
    const byId = new Map()
    items.forEach((r) => {
      const id = String(r.budgetCenterId)
      if (r.budgetCenterId != null && !byId.has(id)) {
        byId.set(id, { value: id, label: r.budgetCenterName, region: regionById[id] || "—" })
      }
    })
    return [...byId.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)))
  }, [items, centers])

  const regions = useMemo(
    () =>
      [...new Set(options.map((o) => o.region))]
        .filter((name) => name !== "—")
        .sort()
        .map((name) => ({ name, ids: options.filter((o) => o.region === name).map((o) => o.value) })),
    [options]
  )

  const filtered = useMemo(() => {
    if (!selected.length) return items
    const keep = new Set(selected)
    return items.filter((r) => keep.has(String(r.budgetCenterId)))
  }, [items, selected])

  return { filtered, pickerProps: { options, regions, selected, setSelected } }
}

export const CenterPicker = ({ options, regions, selected, setSelected }) => {
  const isOnly = (ids) => ids.length === selected.length && ids.every((id) => selected.includes(id))
  return (
    <div className="br-center-picker">
      <div className="br-center-select">
        <MultiSelect
          options={options}
          value={selected}
          onChange={setSelected}
          placeholder="All centers"
          searchPlaceholder="Search center"
        />
      </div>
      {regions.map((region) => (
        <Button
          key={region.name}
          size="sm"
          variant={isOnly(region.ids) ? "primary" : "secondary"}
          onClick={() => setSelected(region.ids)}
        >
          {region.name} ({region.ids.length})
        </Button>
      ))}
      {selected.length > 0 && (
        <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
          All centers
        </Button>
      )}
    </div>
  )
}
